if (!customElements.get('motion-element')) {

const motionReduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const imageReady = (img) =>
  new Promise((resolve) => {
    if (!img || img.complete) return resolve();
    img.addEventListener('load', () => resolve(), { once: true });
    img.addEventListener('error', () => resolve(), { once: true });
  });

class MotionElement extends HTMLElement {
  constructor() {
    super();
    this.preInitialize();
  }

  connectedCallback() {
    if (motionReduced() || !window.Motion) return;
    window.Motion.inView(
      this,
      async () => {
        if (!this.isInstant && this.media) await imageReady(this.media);
        this.initialize();
      },
      { margin: '0px 0px -50px 0px' }
    );
  }

  get isHold() {
    return this.hasAttribute('hold');
  }

  get isInstant() {
    return this.hasAttribute('data-instantly');
  }

  get media() {
    return this.querySelector('img, iframe, svg');
  }

  get animationType() {
    return this.dataset.motion || 'fade-up';
  }

  get animationDelay() {
    return parseInt(this.dataset.motionDelay || 0) / 1000;
  }

  preInitialize() {
    if (this.isHold || motionReduced() || !window.Motion) return;
    switch (this.animationType) {
      case 'fade-in':
        window.Motion.animate(this, { opacity: 0.01 }, { duration: 0 });
        break;
      case 'fade-up':
        window.Motion.animate(this, { transform: 'translateY(2.5rem)', opacity: 0.01 }, { duration: 0 });
        break;
      case 'zoom-in':
        window.Motion.animate(this, { transform: 'scale(0.8)', opacity: 0.01 }, { duration: 0 });
        break;
      case 'zoom-out':
        window.Motion.animate(this, { transform: 'scale(1.15)' }, { duration: 0 });
        break;
      case 'zoom-out-sm':
        window.Motion.animate(this, { transform: 'scale(1.08)' }, { duration: 0 });
        break;
    }
  }

  async initialize() {
    if (this.isHold || this._animating || motionReduced() || !window.Motion) return;
    this._animating = true;
    const easing = [0, 0, 0.3, 1];
    switch (this.animationType) {
      case 'fade-in':
        await window.Motion.animate(this, { opacity: 1 }, { duration: 1.5, delay: this.animationDelay, easing }).finished;
        break;
      case 'fade-up':
        await window.Motion.animate(this, { transform: 'translateY(0)', opacity: 1 }, { duration: 0.5, delay: this.animationDelay, easing }).finished;
        break;
      case 'zoom-in':
        await window.Motion.animate(this, { transform: 'scale(1)', opacity: 1 }, { duration: 1.3, delay: this.animationDelay, easing }).finished;
        break;
      case 'zoom-out':
        await window.Motion.animate(this, { transform: 'scale(1)' }, { duration: 1.6, delay: this.animationDelay, easing }).finished;
        break;
      case 'zoom-out-sm':
        await window.Motion.animate(this, { transform: 'scale(1)' }, { duration: 1, delay: this.animationDelay, easing }).finished;
        break;
    }
    this._animating = false;
    this.classList.add('is-animated');
  }

  async resetAnimation(duration) {
    if (!window.Motion) return;
    const easing = duration ? 'linear' : [0, 0, 0.3, 1];
    switch (this.animationType) {
      case 'fade-in':
        await window.Motion.animate(this, { opacity: 0 }, { duration: duration || 1.5, easing }).finished;
        break;
      case 'fade-up':
        await window.Motion.animate(this, { transform: 'translateY(2.5rem)', opacity: 0 }, { duration: duration || 0.5, easing }).finished;
        break;
      case 'zoom-in':
        await window.Motion.animate(this, { transform: 'scale(0.8)', opacity: 0 }, { duration: duration || 1, easing }).finished;
        break;
      case 'zoom-out':
        await window.Motion.animate(this, { transform: 'scale(1.15)' }, { duration: duration || 1, easing }).finished;
        break;
      case 'zoom-out-sm':
        await window.Motion.animate(this, { transform: 'scale(1.08)' }, { duration: duration || 1, easing }).finished;
        break;
    }
  }

  refreshAnimation() {
    this._animating = false;
    this.removeAttribute('hold');
    this.preInitialize();
    setTimeout(() => this.initialize(), 50);
  }
}

  customElements.define('motion-element', MotionElement);

}
